import {
    Tooltip,
    Bar,
    XAxis,
    YAxis,
    Line,
    ComposedChart,
    Legend,
    CartesianGrid,
    Area,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts'

import {SocketSubMessage} from '../backend/logStream'
import Text from '../../components/text/Text'
import {Headers} from 'react-csv/lib/core'

export interface CComposedDataSet {
    name: string
    bitcoin: number
    short_count: number
    long_count: number
    long_short_ratio: number
    entry_short_volume: number
    entry_long_volume: number
    short_volume: number
    long_volume: number
    holding_long_volume: number
    holding_short_volume: number
    holding: number

    long_short_count_ratio: number
    long_short_count_sum: number

    long_short_volume_ratio: number
    long_short_volume_sum: number

    holding_btc_volume: number
    holding_alt_volume: number
    btc_alt_volume_ratio: number
    holding_long_short_volume_sum: number
}

export interface IComposeChartProps {
    title?: string
    dataset?: CComposedDataSet[]
    headers?: Headers
    csvData?: any
    onClick?: (e: SocketSubMessage) => void
}

export const CComposedChart: React.FC<IComposeChartProps> = ({title, onClick, dataset}) => {
    const Customizeddot = props => {
        const {cx, cy, value} = props

        if (value > 1) {
            return <></>
        }

        return (
            <svg x={cx - 10} y={cy - 10} width={20} height={20} fill="green" viewBox="0 0 1024 1024">
                <path d="M517.12 53.248q95.232 0 179.2 36.352t145.92 98.304 98.304 145.92 36.352 179.2-36.352 179.2-98.304 145.92-145.92 98.304-179.2 36.352-179.2-36.352-145.92-98.304-98.304-145.92-36.352-179.2 36.352-179.2 98.304-145.92 145.92-98.304 179.2-36.352zM663.552 261.12q-15.36 0-28.16 6.656t-23.04 18.432-15.872 27.648-5.632 33.28q0 35.84 21.504 61.44t51.2 25.6 51.2-25.6 21.504-61.44q0-17.408-5.632-33.28t-15.872-27.648-23.04-18.432-28.16-6.656zM373.76 261.12q-29.696 0-50.688 25.088t-20.992 60.928 20.992 61.44 50.688 25.6 50.176-25.6 20.48-61.44-20.48-60.928-50.176-25.088zM520.192 602.112q-51.2 0-97.28 9.728t-82.944 27.648-62.464 41.472-35.84 51.2q-1.024 1.024-1.024 2.048-1.024 3.072-1.024 8.704t2.56 11.776 7.168 11.264 12.8 6.144q25.6-27.648 62.464-50.176 31.744-19.456 79.36-35.328t114.176-15.872q67.584 0 116.736 15.872t81.92 35.328q37.888 22.528 63.488 50.176 17.408-5.12 19.968-18.944t0.512-18.944-3.072-7.168-1.024-3.072q-26.624-55.296-100.352-88.576t-176.128-33.28z" />
            </svg>
        )
    }

    return (
        <div className="relative">
            <Text children={`${'Position entry order count(BTC/ALT)'}`} className="m-[1rem]" />
            <ResponsiveContainer width={1300} height={800}>
                <ComposedChart syncId={title} width={1300} height={800} data={dataset}>
                    <XAxis dataKey="name" />
                    {bitcoinAxis({})}
                    {ratioAxis({name: 'Long/Short Count ratio'})}
                    <YAxis
                        yAxisId="right"
                        stroke="#5cd65c"
                        label={{
                            offset: 5,
                            value: 'Count Index',
                            fill: '#5cd65c',
                            angle: 90,
                            position: 'center',
                        }}
                        orientation="right"
                    />

                    <ReferenceLine y={1} yAxisId="ratio" stroke="#000" />
                    {assetsAxis({})}
                    <Tooltip />
                    <Legend />
                    <CartesianGrid stroke="#f5f5f5" />
                    {assetsArea({})}
                    <Bar yAxisId="right" dataKey="short_count" barSize={20} stackId="1" fill="#a0413e" />
                    <Bar yAxisId="right" dataKey="long_count" barSize={20} stackId="1" fill="#5cd65c" />
                    <Line yAxisId="left" type="monotone" dataKey="bitcoin" stroke="#ff7300" strokeWidth={3} />
                </ComposedChart>
            </ResponsiveContainer>
            <Text children={`${'Position entry order count ratio, sum(BTC/ALT)'}`} className="m-[1rem]" />
            <ResponsiveContainer width={1300} height={800}>
                <ComposedChart syncId={title} width={1300} height={800} data={dataset}>
                    <XAxis dataKey="name" />
                    {bitcoinAxis({})}
                    {ratioAxis({name: 'Long/Short Count ratio'})}
                    <ReferenceLine y={1} yAxisId="ratio" stroke="#000" />
                    {assetsAxis({})}
                    <YAxis
                        yAxisId="right"
                        stroke="#000"
                        label={{
                            offset: 5,
                            value: 'Count Sum',
                            fill: '#000',
                            angle: 90,
                            position: 'center',
                        }}
                        orientation="right"
                    />
                    <ReferenceLine y={1} yAxisId="right" stroke="#000" />
                    <Tooltip />
                    <Legend />
                    <CartesianGrid stroke="#f5f5f5" />
                    {assetsArea({})}
                    <Line yAxisId="left" type="monotone" dataKey="bitcoin" stroke="#ff7300" strokeWidth={3} />
                    <Line
                        yAxisId="ratio"
                        type="monotone"
                        name="Long Short count ratio"
                        dataKey="long_short_count_ratio"
                        stroke="#b5eaaa"
                        strokeWidth={2}
                    />
                    <Line
                        dot={<div></div>}
                        yAxisId="right"
                        type="monotone"
                        name="Long Short count sum"
                        dataKey="long_short_count_sum"
                        stroke="#000099"
                        strokeWidth={2}
                    />
                </ComposedChart>
            </ResponsiveContainer>
            <Text children={`${'Position entry volume(BTC/ALT)'}`} className="m-[1rem]" />
            <ResponsiveContainer width={1300} height={800}>
                <ComposedChart syncId={title} width={1300} height={800} data={dataset}>
                    <XAxis dataKey="name" />
                    {bitcoinAxis({})}
                    <YAxis
                        yAxisId="leftVolume"
                        stroke="#5cd65c"
                        label={{
                            offset: 5,
                            value: 'Open Position Order Vloume',
                            fill: '#5cd65c',
                            angle: 90,
                            position: 'center',
                        }}
                        orientation="right"
                    />
                    {ratioAxis({name: 'Long/Short ratio'})}
                    {assetsAxis({})}
                    <Tooltip />
                    <Legend />
                    <CartesianGrid stroke="#f5f5f5" />
                    {assetsArea({})}
                    <Line yAxisId="left" type="monotone" dataKey="bitcoin" stroke="#ff7300" strokeWidth={3} />
                    <Area
                        yAxisId="leftVolume"
                        dot={<div></div>}
                        activeDot={false}
                        type="monotone"
                        dataKey="long_volume"
                        stroke="#5cd65c"
                        fill="#5cd65c"
                    />
                    <Area
                        yAxisId="leftVolume"
                        dot={<div></div>}
                        activeDot={false}
                        type="monotone"
                        dataKey="short_volume"
                        stroke="#a0413e"
                        fill="#a0413e"
                    />
                </ComposedChart>
            </ResponsiveContainer>
            <Text children={`${'Position entry order volunme ratio sum(BTC/ALT)'}`} className="m-[1rem]" />
            <ResponsiveContainer width={1300} height={800}>
                <ComposedChart syncId={title} width={1300} height={800} data={dataset}>
                    <XAxis dataKey="name" />
                    {bitcoinAxis({})}
                    {ratioAxis({name: 'Long/Short Count ratio'})}
                    <ReferenceLine y={1} yAxisId="ratio" stroke="#000" />
                    {assetsAxis({})}
                    <YAxis
                        yAxisId="right"
                        stroke="#000"
                        label={{
                            offset: 5,
                            value: 'Volume Sum',
                            fill: '#000',
                            angle: 90,
                            position: 'center',
                        }}
                        orientation="right"
                    />
                    <ReferenceLine y={1} yAxisId="right" stroke="#000" />
                    <Tooltip />
                    <Legend />
                    <CartesianGrid stroke="#f5f5f5" />
                    {assetsArea({})}
                    <Line yAxisId="left" type="monotone" dataKey="bitcoin" stroke="#ff7300" strokeWidth={3} />
                    <Line
                        yAxisId="ratio"
                        type="monotone"
                        name="Long Short Volume ratio"
                        dataKey="long_short_volume_ratio"
                        stroke="#b5eaaa"
                        strokeWidth={2}
                    />
                    <Line
                        dot={<div></div>}
                        yAxisId="right"
                        type="monotone"
                        name="Lomg Short Volume sum"
                        dataKey="long_short_volume_sum"
                        stroke="#000099"
                        strokeWidth={2}
                    />
                </ComposedChart>
            </ResponsiveContainer>
            <Text children={`${'Position Holding volume BTC, ALT'}`} className="m-[1rem]" />
            <ResponsiveContainer width={1300} height={800}>
                <ComposedChart syncId={title} width={1300} height={800} data={dataset}>
                    <XAxis dataKey="name" />
                    {bitcoinAxis({})}
                    <YAxis
                        yAxisId="right"
                        stroke="#5cd65c"
                        label={{
                            offset: 5,
                            value: 'Holding Position Volume',
                            fill: '#5cd65c',
                            angle: 90,
                            position: 'center',
                        }}
                        orientation="right"
                    />
                    {ratioAxis({name: 'BTC / ALT ratio'})}
                    {assetsAxis({})}
                    <Tooltip />
                    <Legend />
                    <CartesianGrid stroke="#f5f5f5" />
                    <ReferenceLine y={0} yAxisId="right" stroke="#000" />
                    <ReferenceLine y={1} yAxisId="ratio" stroke="#000" />
                    {assetsArea({})}
                    <Bar
                        yAxisId="right"
                        name="Holding BTC Volume"
                        dataKey="holding_btc_volume"
                        barSize={20}
                        fill="#5cd65c"
                    />
                    <Bar
                        yAxisId="right"
                        name="Holding alt Volume"
                        dataKey="holding_alt_volume"
                        barSize={20}
                        fill="#a0413e"
                    />
                    <Line
                        yAxisId="ratio"
                        type="monotone"
                        name="BTC ALT Volume ratio"
                        dataKey="btc_alt_volume_ratio"
                        stroke="#b5eaaa"
                        strokeWidth={2}
                    />
                    <Line yAxisId="left" type="monotone" dataKey="bitcoin" stroke="#ff7300" strokeWidth={3} />
                </ComposedChart>
            </ResponsiveContainer>
            <Text children={`${'Position Holding volume Long, Short'}`} className="m-[1rem]" />
            <ResponsiveContainer width={1300} height={800}>
                <ComposedChart syncId={title} width={1300} height={800} data={dataset}>
                    <XAxis dataKey="name" />
                    <YAxis
                        yAxisId="right"
                        stroke="#5cd65c"
                        label={{
                            offset: 5,
                            value: 'Holding Position Volume',
                            fill: '#5cd65c',
                            angle: 90,
                            position: 'center',
                        }}
                        orientation="right"
                    />
                    {bitcoinAxis({})}
                    {assetsAxis({})}
                    <Tooltip />
                    <Legend />
                    <CartesianGrid stroke="#f5f5f5" />
                    {assetsArea({})}
                    {ratioAxis({name: 'Volume sum'})}
                    <ReferenceLine y={0} yAxisId="right" stroke="#000" />
                    <Bar
                        yAxisId="right"
                        name="Holding Long Volume"
                        dataKey="holding_long_volume"
                        barSize={20}
                        fill="#5cd65c"
                    />
                    <Bar
                        yAxisId="right"
                        name="Holding Short Volume"
                        dataKey="holding_short_volume"
                        barSize={20}
                        fill="#a0413e"
                    />
                    <Line
                        yAxisId="ratio"
                        type="monotone"
                        name="Holding Long Short Volume sum"
                        dataKey="holding_long_short_volume_sum"
                        stroke="#000099"
                        strokeWidth={2}
                    />
                    <Line yAxisId="left" type="monotone" dataKey="bitcoin" stroke="#ff7300" strokeWidth={3} />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    )
}

const bitcoinAxis: React.FC = () => {
    return (
        <YAxis
            stroke="#ff7300"
            label={{
                fill: '#ff7300',
                value: 'Bitcoin',
                angle: -90,
            }}
            yAxisId="left"
            domain={[23000, 'dataMax+1000']}
        />
    )
}

interface IRatioAxisProps {
    name: string
}

const ratioAxis: React.FC<IRatioAxisProps> = ({name}) => {
    return (
        <YAxis
            yAxisId="ratio"
            stroke="#b5eaaa"
            label={{
                offset: 5,
                value: name,
                fill: '#b5eaaa',
                angle: 90,
                position: 'center',
            }}
            domain={[0, 'dataMax+1']}
            orientation="right"
        />
    )
}

const assetsArea: React.FC = () => {
    return <Area yAxisId="rightend" name="Assets" type="monotone" dataKey="holding" fill="#8884d8" stroke="#8884d8" />
}

const assetsAxis: React.FC = () => {
    return (
        <YAxis
            stroke="#8884d8"
            yAxisId="rightend"
            orientation="right"
            label={{value: 'Assets', fill: '#8884d8', angle: 90, position: 'insideRight'}}
        />
    )
}
